import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './api'
import { DEFAULT_NAV_ORDER, NAV_BY_ID } from './nav'
import { getLocale, isLocale, markLocaleChoice, setLocale, takeLocaleChoice, type Locale } from '@/i18n/locale'

export type ThemeMode = 'light' | 'dark'

export interface SidebarPrefs {
  /** Ordered list of nav item ids. */
  order: string[]
  /** Nav item ids the user has switched off. */
  hidden: string[]
  /** Sidebar collapsed to an icon rail. */
  collapsed: boolean
}

export interface Preferences {
  sidebar: SidebarPrefs
  theme: ThemeMode
  favorites: string[]
  locale?: Locale
}

const STORAGE_KEY = 'igf-prefs'

const DEFAULT_SIDEBAR: SidebarPrefs = {
  order: DEFAULT_NAV_ORDER,
  hidden: [],
  collapsed: false,
}

const DEFAULT_PREFS: Preferences = { sidebar: DEFAULT_SIDEBAR, theme: 'light', favorites: [] }

function normalizeSidebar(raw: Partial<SidebarPrefs> | undefined): SidebarPrefs {
  const known = new Set(Object.keys(NAV_BY_ID))
  const requested = Array.isArray(raw?.order) ? raw!.order.filter((id) => known.has(id)) : []
  const order = [...requested, ...DEFAULT_NAV_ORDER.filter((id) => !requested.includes(id))]
  const hidden = Array.isArray(raw?.hidden) ? raw!.hidden.filter((id) => known.has(id)) : []
  return { order, hidden, collapsed: Boolean(raw?.collapsed) }
}

function normalizePrefs(raw: Partial<Preferences> | undefined): Preferences {
  const favorites = Array.isArray(raw?.favorites)
    ? [...new Set(raw!.favorites.filter((id) => typeof id === 'string' && id.length > 0))]
    : []
  return {
    sidebar: normalizeSidebar(raw?.sidebar),
    theme: raw?.theme === 'dark' ? 'dark' : 'light',
    favorites,
    ...(isLocale(raw?.locale) ? { locale: raw.locale } : {}),
  }
}

function devicePrefs(current: Preferences): Preferences {
  return { ...DEFAULT_PREFS, theme: current.theme, ...(current.locale ? { locale: current.locale } : {}) }
}

export function keepDevicePrefsOnly() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(devicePrefs(readLocal())))
  } catch {
    return
  }
}

function readLocal(): Preferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULT_PREFS
    return normalizePrefs(JSON.parse(raw) as Partial<Preferences>)
  } catch {
    return DEFAULT_PREFS
  }
}

interface PreferencesContextValue {
  sidebar: SidebarPrefs
  theme: ThemeMode
  setOrder: (order: string[]) => void
  toggleHidden: (id: string) => void
  setHidden: (id: string, hidden: boolean) => void
  setCollapsed: (collapsed: boolean) => void
  resetSidebar: () => void
  setTheme: (theme: ThemeMode) => void
  toggleTheme: () => void
  setLanguage: (locale: Locale) => void
  favorites: string[]
  isFavorite: (companyId: string) => boolean
  toggleFavorite: (companyId: string) => void
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null)

export function PreferencesProvider({
  authed,
  children,
}: {
  authed: boolean
  children: ReactNode
}) {
  const queryClient = useQueryClient()
  const [prefs, setPrefs] = useState<Preferences>(() => readLocal())
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const serverApplied = useRef(false)

  const settingsQuery = useQuery({
    queryKey: ['me-settings'],
    queryFn: () => api.getSettings(),
    enabled: authed,
    staleTime: 5 * 60 * 1000,
  })

  // Apply theme to <html> so the CSS tokens switch app-wide.
  useEffect(() => {
    document.documentElement.dataset.theme = prefs.theme
  }, [prefs.theme])

  const persist = useCallback(
    (changed: Preferences) => {
      const next: Preferences = { ...changed, locale: getLocale() }
      setPrefs(next)
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
      } catch {
        /* ignore quota / private mode */
      }
      if (authed) {
        queryClient.setQueryData(['me-settings'], { preferences: next })
        if (saveTimer.current) clearTimeout(saveTimer.current)
        saveTimer.current = setTimeout(() => {
          api.saveSettings(next as unknown as Record<string, unknown>).catch(() => {})
        }, 600)
      }
    },
    [authed, queryClient]
  )

  // Pull server-side prefs once after login; they win over the local cache.
  useEffect(() => {
    if (serverApplied.current || !settingsQuery.data) return
    serverApplied.current = true
    const server = settingsQuery.data.preferences as Partial<Preferences> | undefined
    const hasServerPrefs = Boolean(server && (server.sidebar || server.theme || server.favorites || server.locale))
    const base = hasServerPrefs ? normalizePrefs(server) : prefs
    const chosen = takeLocaleChoice()
    if (chosen) {
      setLocale(chosen)
      persist(base)
      return
    }
    if (!hasServerPrefs) return
    setPrefs(base)
    if (base.locale) setLocale(base.locale)
  }, [settingsQuery.data, prefs, persist])

  const value = useMemo<PreferencesContextValue>(() => {
    const updateSidebar = (patch: Partial<SidebarPrefs>) =>
      persist({ ...prefs, sidebar: normalizeSidebar({ ...prefs.sidebar, ...patch }) })

    return {
      sidebar: prefs.sidebar,
      theme: prefs.theme,
      favorites: prefs.favorites,
      isFavorite: (companyId) => prefs.favorites.includes(companyId),
      toggleFavorite: (companyId) =>
        persist({
          ...prefs,
          favorites: prefs.favorites.includes(companyId)
            ? prefs.favorites.filter((id) => id !== companyId)
            : [...prefs.favorites, companyId],
        }),
      setOrder: (order) => updateSidebar({ order }),
      toggleHidden: (id) =>
        updateSidebar({
          hidden: prefs.sidebar.hidden.includes(id)
            ? prefs.sidebar.hidden.filter((h) => h !== id)
            : [...prefs.sidebar.hidden, id],
        }),
      setHidden: (id, hidden) =>
        updateSidebar({
          hidden: hidden
            ? [...new Set([...prefs.sidebar.hidden, id])]
            : prefs.sidebar.hidden.filter((h) => h !== id),
        }),
      setCollapsed: (collapsed) => updateSidebar({ collapsed }),
      resetSidebar: () => persist({ ...prefs, sidebar: DEFAULT_SIDEBAR }),
      setTheme: (theme) => persist({ ...prefs, theme }),
      toggleTheme: () => persist({ ...prefs, theme: prefs.theme === 'dark' ? 'light' : 'dark' }),
      setLanguage: (locale) => {
        setLocale(locale)
        persist(prefs)
      },
    }
  }, [prefs, persist])

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>
}

const DETACHED: PreferencesContextValue = {
  sidebar: DEFAULT_SIDEBAR,
  theme: 'light',
  favorites: [],
  isFavorite: () => false,
  toggleFavorite: () => {},
  setOrder: () => {},
  toggleHidden: () => {},
  setHidden: () => {},
  setCollapsed: () => {},
  resetSidebar: () => {},
  setTheme: () => {},
  toggleTheme: () => {},
  setLanguage: (locale) => {
    markLocaleChoice(locale)
    setLocale(locale)
  },
}

export function usePreferences(): PreferencesContextValue {
  return useContext(PreferencesContext) ?? DETACHED
}
